import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FAQ_ITEMS, type FaqItem } from "./faqData.js";
import { PAGE_GUTTER_INSET_CLASS } from "../../shared/PageContainer.js";

const PawIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`lucide lucide-paw-print-icon lucide-paw-print ${className ?? "w-6 h-6"}`}
  >
    <circle cx="11" cy="4" r="2" />
    <circle cx="18" cy="8" r="2" />
    <circle cx="20" cy="16" r="2" />
    <path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z" />
  </svg>
);

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace("#", "");
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const PAW_ICONS = [
  // order matters for stagger: id 1 first, id 4 second
  // tx/ty are translate offsets (in rem) for the visible position.
  { id: 1, rotation: -18, delayMs: 0, tx: 0, ty: 0 },
  { id: 4, rotation: 12, delayMs: 90, tx: -2.5, ty: -0.15 },
  { id: 2, rotation: -4, delayMs: 180, tx: -5, ty: 0.12 },
  { id: 3, rotation: 18, delayMs: 270, tx: -7.5, ty: -0.6 },
];

const FaqAccordion = () => {
  const { t } = useTranslation("landing");
  const [activeId, setActiveId] = useState<string | null>(
    FAQ_ITEMS[0]?.id ?? null,
  );
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const toggleItem = (id: string) => {
    setActiveId((current) => (current === id ? null : id));
  };

  const renderItem = (item: FaqItem) => {
    const isActive = activeId === item.id;
    const isHovered = hoveredId === item.id;

    const baseTitleClasses =
      "group flex items-center justify-between w-full rounded-2xl border px-4 md:px-5 py-3 md:py-4 text-sm md:text-base font-semibold transition-all duration-200 cursor-pointer backdrop-blur-sm";

    const activeTitleClasses = isActive
      ? " text-white shadow-lg"
      : " hover:shadow-md";

    return (
      <div key={item.id} className="mb-3">
        <button
          type="button"
          onClick={() => toggleItem(item.id)}
          onMouseEnter={() => setHoveredId(item.id)}
          onMouseLeave={() =>
            setHoveredId((current) => (current === item.id ? null : current))
          }
          className={baseTitleClasses + activeTitleClasses}
          style={{
            borderColor: item.color,
            backgroundColor: isActive
              ? item.color
              : hexToRgba(item.color, hoveredId === item.id ? 0.18 : 0.1),
            color: isActive ? "#ffffff" : item.color,
          }}
          aria-expanded={isActive}
          aria-controls={`faq-content-${item.id}`}
        >
          <span className="flex items-center gap-2 text-left">
            <span className="shrink-0">{item.icon}</span>
            <span>{t(`faq.items.${item.i18nKey}.title`)}</span>
          </span>
          <span className="relative flex items-center justify-center w-6 md:w-7 h-6 md:h-7 ml-3">
            <span
              className={
                "absolute text-xs md:text-sm transition-all duration-200 ease-out" +
                (isActive || isHovered
                  ? " opacity-0 -translate-x-1"
                  : " opacity-100 translate-x-0")
              }
            >
              ▼
            </span>
            {PAW_ICONS.map((cfg, index) => {
              const visible = isActive || isHovered;
              // Use per-paw translate offsets from cfg to create a staggered 2D motion.
              const hiddenTransform = `translate(${cfg.tx + 0.5}rem, ${cfg.ty - 0.12}rem) scale(0.75) rotate(${cfg.rotation}deg)`;
              const visibleTransform = `translate(${cfg.tx}rem, ${cfg.ty}rem) scale(1) rotate(${cfg.rotation}deg)`;
              // show only first two paws on small screens
              const responsiveHideClass =
                index >= 2 ? "hidden md:inline-block" : "inline-block";
              return (
                <span
                  key={cfg.id}
                  className={`absolute transition-all duration-300 ease-out ${responsiveHideClass}`}
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? visibleTransform : hiddenTransform,
                    transitionDelay: visible ? `${cfg.delayMs}ms` : "0ms",
                    left: 0,
                  }}
                >
                  <PawIcon className="w-4 h-4 md:w-6 md:h-6 text-current" />
                </span>
              );
            })}
          </span>
        </button>
        <div
          id={`faq-content-${item.id}`}
          className={
            "overflow-hidden transition-all duration-300 ease-out text-[0.9rem] md:text-sm" +
            (isActive
              ? " max-h-80 opacity-100 mt-3 mb-3"
              : " max-h-0 opacity-0")
          }
        >
          <div
            className="mx-auto w-[95%] rounded-2xl border border-t-0 px-4 md:px-5 py-3 md:py-4 text-sm leading-relaxed bg-white/90 text-my-black"
            style={{ borderColor: item.color }}
          >
            {t(`faq.items.${item.i18nKey}.content`)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={PAGE_GUTTER_INSET_CLASS}>
      <div className="max-w-3xl mx-auto">
        {/* Main title with decorative lines, inspired by the reference CSS */}
        <div className="flex items-center justify-center mb-6 md:mb-8">
          <span className="hidden sm:block flex-1 h-px bg-main-blue/70" />
          <h2 className="mx-4 text-center font-bold text-2xl md:text-4xl text-my-black tracking-tight">
            {t("faq.title")}
          </h2>
          <span className="hidden sm:block flex-1 h-px bg-main-blue/70" />
        </div>

        {/* Wrapper similarly constrained like the CSS .wrapper */}
        <div className="mx-auto w-full md:max-w-2xl">
          {FAQ_ITEMS.map(renderItem)}
        </div>
      </div>
    </div>
  );
};

export default FaqAccordion;
